import {
  CompanyGroup,
  InvesteeDTO,
  InvesteeShareHoldingDetails,
  ShareMetrics,
} from '.';
import { ShareMovementType } from '../share-movement';

export type GetInvesteeResponse = InvesteeDTO &
  ShareMetrics & {
    currentSharePrice: number;
  };

export interface ListInvesteeQuery {
  firmId?: string;
  fundId?: string;
  perPage: string;
  page: string;
  type: 'asc' | 'desc';
  keyName: string;
  companyName?: string;
  orderBy: string;
  currentShareholdingsOnly?: boolean;
}
export interface ListInvesteeResponse {
  total: number;
  perPage: number;
  result: Array<
    Pick<
      InvesteeDTO,
      | '_id'
      | 'firm'
      | 'fund'
      | 'tradingName'
      | 'shareClass'
      | 'isVCT'
      | 'legalName'
    > & {
      lastAllocationDate?: Date;
    }
  >;
}

export type GetGroupedCompaniesQuery = Omit<ListInvesteeQuery, 'keyName'>;

export type ExportCompanyListQuery = Omit<
  ListInvesteeQuery,
  'perPage' | 'page' | 'keyName'
>;

export interface GetInvesteeShareholdingsQuery {
  pageSize: string;
  page: string;
  orderBy: string;
  companyId: string;
  order?: 'asc' | 'desc';
  tradingName?: string;
  firmId?: string;
  search?: string;
}

export type ExportInvesteeShareholdingsQuery = Omit<
  GetInvesteeShareholdingsQuery,
  'pageSize' | 'page'
> & {
  tradingName: string;
};

export interface GetInvesteeShareHoldingsResult {
  perPage: number;
  total: number;
  result: Array<
    InvesteeShareHoldingDetails & {
      investor: { fullName: string };
      investment: { investmentDate: Date; externalInvestmentId: string };
      fund: { fundName: string };
      shareMovements: Array<{
        _id: string;
        companyId: string;
        exitId: string;
        type: ShareMovementType;
        date: string;
        noOfShares: number;
        investorId: string;
      }>;
    }
  >;
}

export interface GetGroupedCompaniesResult {
  total: number;
  perPage: number;
  result: Array<CompanyGroup>;
}

export interface UpdateInvesteeRequest
  extends Omit<
    InvesteeDTO,
    'revaluation' | 'linkedinUrl' | 'twitterUrl' | 'url' | '_id'
  > {
  linkedinUrl: string;
  twitterUrl: string;
  url: string;
  fundId?: string;
  firmId?: string;
  revaluation?: Array<{
    amount: number;
    date: string;
    id: string;
    _id: string;
    setVia?: string;
  }>;
}

export enum Sector {
  ADVANCED_MATERIALS = 'Advanced Materials',
  AEROSPACE = 'Aerospace',
  AI = 'Artificial Intelligence',
  APPLIEDAI = 'Applied AI',
  APPLIEDPHYSICS = 'Applied Physics',
  BIOTECH = 'Biotech',
  BLOCKCHAIN = 'Blockchain',
  CLOUD_DATA_INFRASTRUCTURE = 'Cloud & Data Infrastructure',
  COMMERCE = 'Commerce',
  CONSTRUCTION = 'Construction',
  CONSUMER = 'Consumer',
  CONSUMER_TECHNOLOGY = 'Consumer Technology',
  CYBER_SEC = 'Cyber Security',
  C_AND_D = 'Content & Design',
  DATA_DRIVEN_HEALTH = 'Data-Driven Health',
  DEEPTECH = 'DeepTech',
  DEVOPS = 'DevOps',
  EDTECH = 'Edtech',
  EDU = 'Education',
  ENERGY = 'Energy',
  ENTERPRISE_SOFTWARE = 'Enterprise Software',
  FINTECH = 'Fintech',
  FIN_SERVICES = 'Financial Services',
  FOODTECH = 'Foodtech',
  FUTUREOFWORK = 'Future of work',
  GAMING = 'Gaming',
  HEALTHCARE = 'Healthcare',
  HEALTHTECH = 'Healthtech',
  HR = 'HR',
  HRTECH = 'HRtech',
  INDUSTRIAL = 'Industrial Applications',
  INSURTECH = 'Insurtech',
  IOT = 'IoT',
  LIFE_SCI = 'Life Sciences',
  LOGISTICS = 'Logistics',
  MARKETING_TECH = 'Marketing Tech',
  MEDIA = 'Media',
  MIDDLEWARE = 'Middleware',
  MOBILITY = 'Mobility',
  NEXT_GEN_MEDIA = 'Next Gen Media',
  PHOTONICS = 'Photonics',
  PRODUCTTOOLS = 'Product Tools',
  PROPTECH = 'Proptech',
  REAL_ESTATE = 'Real Estate',
  RETAILTECH = 'Retail Tech',
  REV_OPS = 'RevOps',
  ROBOTICS = 'Robotics',
  SAAS = 'SaaS',
  SALES = 'Sales',
  SALES_ENAB = 'Sales Enablement',
  SECURITY = 'Security',
  SPORTS = 'Sports',
  SUSTAINABILITY = 'Sustainability',
  TELECOMS = 'Telecommunications',
  TRANSPORT = 'Transport',
  TRAVEL = 'Travel',
  UTILITIES = 'Utilities',
  VERTICALSAAS = 'Vertical SaaS',
  WEB3 = 'Web3',
  OTHER = 'Other',
}

export enum Stage {
  PRE_SEED = 'Pre-Seed',
  SEED = 'Seed',
  PRE_SERIES_A = 'Pre-Series A',
  SERIES_A = 'Series A',
  SERIES_B = 'Series B',
  SERIES_C = 'Series C',
  SERIES_D = 'Series D',
  SERIES_E = 'Series E+',
}
